import React, { useEffect, useMemo, useState } from "react";

/*
 * Present value / future value factor tables.
 * Look up a factor like a printed table, click a cell to multiply it by
 * an amount, or type an exact rate and number of periods.
 */

const MAX_RATE = 20; // columns: 1% ... 20%
const MAX_PERIODS = 30; // rows: 1 ... 30

const TABLES = [
  {
    id: "pv",
    label: "PV of $1",
    title: "Present value of $1  =  (1 + r)^-n",
    fn: (r, n) => Math.pow(1 + r, -n),
  },
  {
    id: "pva",
    label: "PV of annuity",
    title: "Present value of an annuity of $1  =  (1 - (1 + r)^-n) / r",
    fn: (r, n) => (1 - Math.pow(1 + r, -n)) / r,
  },
  {
    id: "fv",
    label: "FV of $1",
    title: "Future value of $1  =  (1 + r)^n",
    fn: (r, n) => Math.pow(1 + r, n),
  },
  {
    id: "fva",
    label: "FV of annuity",
    title: "Future value of an annuity of $1  =  ((1 + r)^n - 1) / r",
    fn: (r, n) => (Math.pow(1 + r, n) - 1) / r,
  },
];

const rates = Array.from({ length: MAX_RATE }, (_, i) => i + 1);
const periods = Array.from({ length: MAX_PERIODS }, (_, i) => i + 1);

function fmtMoney(value) {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function FactorTables({ onClose }) {
  const [tableId, setTableId] = useState("pv");
  const [amount, setAmount] = useState("1000");
  const [picked, setPicked] = useState(null); // { rate, n }

  const [exactRate, setExactRate] = useState("");
  const [exactPeriods, setExactPeriods] = useState("");

  const table = TABLES.find((t) => t.id === tableId);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pickedFactor = picked
    ? table.fn(picked.rate / 100, picked.n)
    : null;

  const amountNumber = Number(amount);
  const pickedResult =
    pickedFactor !== null && Number.isFinite(amountNumber)
      ? pickedFactor * amountNumber
      : null;

  const exact = useMemo(() => {
    const r = Number(exactRate) / 100;
    const n = Number(exactPeriods);
    if (
      exactRate === "" ||
      exactPeriods === "" ||
      !Number.isFinite(r) ||
      !Number.isFinite(n) ||
      r <= 0 ||
      n <= 0
    ) {
      return null;
    }
    return TABLES.map((t) => ({ id: t.id, label: t.label, value: t.fn(r, n) }));
  }, [exactRate, exactPeriods]);

  return (
    <div className="factor-overlay" role="dialog" aria-label="Factor tables">
      <div className="factor-panel">
        <div className="factor-header">
          <h2 className="h6 fw-bold mb-0">
            <i className="bi bi-table me-2" />
            PV / FV Factor Tables
          </h2>
          <button
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        <div className="factor-tabs">
          {TABLES.map((t) => (
            <button
              key={t.id}
              className={`factor-tab ${t.id === tableId ? "active" : ""}`}
              onClick={() => {
                setTableId(t.id);
                setPicked(null);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="small text-muted mb-2">{table.title}</div>

        <div className="factor-scroll">
          <table className="factor-table">
            <thead>
              <tr>
                <th className="factor-corner">n \ r</th>
                {rates.map((r) => (
                  <th
                    key={r}
                    className={picked?.rate === r ? "factor-hl" : ""}
                  >
                    {r}%
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map((n) => (
                <tr key={n}>
                  <th className={picked?.n === n ? "factor-hl" : ""}>{n}</th>
                  {rates.map((r) => {
                    const isPicked = picked?.rate === r && picked?.n === n;
                    return (
                      <td
                        key={r}
                        className={isPicked ? "factor-picked" : ""}
                        onClick={() => setPicked({ rate: r, n })}
                      >
                        {table.fn(r / 100, n).toFixed(4)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="factor-calc">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small mb-1">Amount</label>
              <input
                className="form-control form-control-sm"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-8">
              {pickedFactor === null ? (
                <div className="small text-muted">
                  Click a cell in the table to multiply its factor by the amount.
                </div>
              ) : (
                <div className="small">
                  <div className="text-muted">
                    {table.label}, {picked.rate}%, {picked.n} period
                    {picked.n > 1 ? "s" : ""}: factor{" "}
                    <strong>{pickedFactor.toFixed(4)}</strong>
                  </div>
                  {pickedResult !== null && (
                    <div className="fw-bold">
                      {fmtMoney(amountNumber)} × {pickedFactor.toFixed(4)} ={" "}
                      {fmtMoney(pickedResult)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <hr className="my-2" />

          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Exact rate (%)</label>
              <input
                className="form-control form-control-sm"
                type="number"
                step="any"
                value={exactRate}
                onChange={(event) => setExactRate(event.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Periods (n)</label>
              <input
                className="form-control form-control-sm"
                type="number"
                step="any"
                value={exactPeriods}
                onChange={(event) => setExactPeriods(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-6">
              {exact ? (
                <div className="small d-flex flex-wrap gap-3">
                  {exact.map((item) => (
                    <span key={item.id}>
                      <span className="text-muted">{item.label}: </span>
                      <strong>{item.value.toFixed(4)}</strong>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="small text-muted">
                  Enter any rate and number of periods for exact factors.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}